import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import { Box } from '@mui/material';

// One array per dialect, named rather than spelled out at the call site.
const GFM = [remarkGfm];
const GFM_WITH_BREAKS = [remarkGfm, remarkBreaks];

/**
 * I12 — markdown is rendered without raw HTML.
 *
 * `rehype-raw` is deliberately NOT installed. react-markdown ignores embedded HTML by
 * default, and that default is the mitigation: ticket bodies are free text, and the
 * moment raw HTML renders, a body becomes a script-injection surface on our own origin.
 * Adding rehype-raw here would be a charter breach, not a formatting improvement.
 *
 * `hardBreaks` turns a single newline into a line break, which markdown otherwise treats
 * as a space. The rule for setting it is the charter's, not each caller's guess
 * (ARCHITECTURE.md standing decision, DWLF-219): ON where the text is a MESSAGE — a
 * comment, a few lines of which every one was meant; OFF where it is a DOCUMENT authored
 * as markdown, where a paragraph wrapped at some column would render ragged.
 *
 * ⚠️ It is a rendering choice, not a property of the stored text, so the archive and any
 * other markdown viewer read a hand-typed comment without these breaks. That divergence is
 * accepted there rather than fixed by rewriting what a caller sent.
 */
export function Markdown({ children, hardBreaks = false }: { children: string; hardBreaks?: boolean }) {
  return (
    <Box
      sx={{
        /**
         * The body is a flex item, and a flex item's automatic minimum size is its
         * CONTENT size — so without `minWidth: 0` a wide table makes the box itself
         * grow and the `overflowX` rules below never engage: the content escapes the
         * panel instead of scrolling inside it. Measured at 720px: a 14-column table took
         * the body to 3100px and the drawer to 3124px; with `minWidth` it holds 672px and
         * the table scrolls itself.
         *
         * `overflowWrap` covers the other offender. A URL wraps without it — `/`, `?` and
         * `:` are break opportunities — but a genuinely unbroken token has none, and a
         * 199-char checksum measured 1731px wide before this line and 672px after.
         */
        minWidth: 0,
        overflowWrap: 'break-word',
        '& h1, & h2, & h3': { mt: 2.5, mb: 1, lineHeight: 1.25 },
        '& h1': { fontSize: '1.4rem' },
        '& h2': { fontSize: '1.15rem' },
        '& h3': { fontSize: '1rem' },
        '& p': { my: 1.25, lineHeight: 1.65 },
        '& ul, & ol': { pl: 3, my: 1.25 },
        '& li': { mb: 0.5 },
        '& code': {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: '0.87em',
          bgcolor: 'action.hover',
          px: 0.6,
          py: 0.15,
          borderRadius: 0.75,
        },
        '& pre': {
          bgcolor: 'action.hover',
          p: 1.5,
          borderRadius: 1,
          overflowX: 'auto',
          '& code': { bgcolor: 'transparent', px: 0 },
        },
        '& blockquote': {
          borderLeft: 3,
          borderColor: 'divider',
          pl: 2,
          ml: 0,
          color: 'text.secondary',
        },
        '& table': { borderCollapse: 'collapse', my: 1.5, display: 'block', overflowX: 'auto' },
        '& th, & td': { border: 1, borderColor: 'divider', px: 1, py: 0.5, textAlign: 'left' },
        '& a': { color: 'primary.main' },
        '& img': { maxWidth: '100%' },
      }}
    >
      <ReactMarkdown remarkPlugins={hardBreaks ? GFM_WITH_BREAKS : GFM}>
        {children}
      </ReactMarkdown>
    </Box>
  );
}
